// Adım 3 — "Kırılımlar" önizlemesi (spec §7.3, DR8 satır türü token'ları): planın yeni kırılım
// satırları (`plan.newBreakdowns`) üst gösterge × boyut başına toplam satırı + grup bandı + üyeler.
// Toplam değeri overlay çözümünden (sunucu sanal grup/üye satırlarıyla hesaplar), üye değeri
// dosyadan. Toplamın altında dosyadaki toplam satırının durumu: eşitse "sistem hesaplar — kaynak n
// = alt kalemler ✓", farklıysa amber "kaynak n ≠ alt kalemler m (fark d)" (asla kırmızı).
// Portalda plan `newBreakdowns` taşımaz → bölüm hiç render edilmez.

import { useMemo } from "react";
import {
  formatKpiNumber,
  isCalculationGroupCandidate,
  type KpiBreakdown,
  type KpiImportColumn,
  type KpiImportGrid,
  type KpiMappingRow,
  type KpiResolvedGrid,
  type KpiResolvedIndicator,
} from "@mahir/shared";
import type { KpiImportPlan, KpiImportPlannedBreakdown } from "../../../api/kpi-import";
import { KPI_BREAKDOWN_COPY } from "../../../lib/kpi-breakdown-copy";
import { breakdownSuggestions, suggestionKey, totalLine } from "../../../lib/kpi-import-breakdown";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import { sheetIndexByRowId } from "../../../lib/kpi-import-wizard";
import { tdStyle, thStyle } from "../kpi-form-styles";
import { Cell } from "./preview-cells";

const b = KPI_IMPORT_COPY.breakdown;

interface GroupView {
  key: string;
  parentCode: string;
  items: KpiImportPlannedBreakdown[];
}

/** Plan satırları → üst × boyut grupları (dosya sırası korunur). */
export function previewBreakdownGroups(list: readonly KpiImportPlannedBreakdown[]): GroupView[] {
  const out = new Map<string, GroupView>();
  for (const p of list) {
    const key = suggestionKey(p);
    const g = out.get(key);
    if (g) g.items.push(p);
    else out.set(key, { key, parentCode: p.parentCode, items: [p] });
  }
  return [...out.values()];
}

const isCandidate = (bd: KpiBreakdown): boolean =>
  isCalculationGroupCandidate({
    id: "",
    parentId: null,
    orderIndex: 0,
    nodeKind: "breakdown_group",
    breakdown: bd,
  });

/**
 * Hesap grubu olacak önizleme grupları (TODO-KPI-133 (a)): üst başına yalnız İLK aday grup — ve
 * yalnız üstün plan dışı (sette zaten olan) aday grubu yoksa; sonraki tam `sum` gruplar doğrulama.
 */
export function previewCalcKeys(
  groups: readonly GroupView[],
  indicators: readonly KpiResolvedIndicator[],
): Set<string> {
  const out = new Set<string>();
  const taken = new Set<string>();
  for (const g of groups) {
    const first = g.items[0]!;
    if (!isCandidate(first) || taken.has(g.parentCode)) continue;
    taken.add(g.parentCode);
    const total = indicators.find((i) => i.code === g.parentCode);
    const planned = new Set(
      groups.filter((x) => x.parentCode === g.parentCode).map((x) => x.items[0]!.dimension),
    );
    const existingCalc =
      total !== undefined &&
      indicators.some(
        (i) =>
          i.parentId === total.id &&
          i.nodeKind === "breakdown_group" &&
          i.breakdown != null &&
          !planned.has(i.breakdown.dimension) &&
          isCandidate(i.breakdown),
      );
    if (!existingCalc) out.add(g.key);
  }
  return out;
}

export function ImportPreviewBreakdownSection(x: {
  plan: KpiImportPlan;
  recalculated: KpiResolvedGrid;
  mapping: readonly KpiMappingRow[];
  grid: KpiImportGrid | null;
  columns: readonly (readonly KpiImportColumn[])[];
}): JSX.Element | null {
  const { plan, recalculated, mapping, grid, columns } = x;
  const groups = useMemo(() => previewBreakdownGroups(plan.newBreakdowns ?? []), [plan]);
  const lines = useMemo(() => {
    if (!grid) return new Map<string, ReturnType<typeof totalLine>>();
    const sheetOfRow = sheetIndexByRowId(grid);
    const gridRows = new Map(grid.sheets.flatMap((s) => s.rows.map((r) => [r.rowId, r] as const)));
    const list = breakdownSuggestions(mapping, {
      gridRows,
      columnsOfRow: (rowId) => columns[sheetOfRow.get(rowId) ?? 0] ?? [],
      sheetOfRow,
      labelByCode: new Map(),
      indicators: [],
    });
    // yalnız kapsamı onaylı gruplar (Adım 2 `totalRowLines` ile aynı): "yalnız gösterim" farkı uyarmaz
    return new Map(list.map((s) => [s.key, s.complete ? totalLine(s.columns) : null]));
  }, [grid, mapping, columns]);
  const indByCode = useMemo(
    () => new Map(recalculated.indicators.map((i) => [i.code, i])),
    [recalculated],
  );
  const entityName = useMemo(
    () => new Map(recalculated.entities.map((e) => [e.id, e.name])),
    [recalculated],
  );
  const calcKeys = previewCalcKeys(groups, recalculated.indicators);
  if (groups.length === 0) return null;

  // Sütunlar: üye hücrelerinin (işletme × yıl) çiftleri
  const memberKeys = new Set(
    groups.flatMap((g) => g.items.map((i) => i.memberCode ?? i.targetKey)),
  );
  const memberCells = plan.cells.filter((c) => memberKeys.has(c.code));
  const cols = [
    ...new Map(memberCells.map((c) => [`${c.entityId}|${c.year.toString()}`, c] as const)).values(),
  ].sort((a, b2) => a.year - b2.year || a.entityId.localeCompare(b2.entityId));
  const multiEntity = new Set(cols.map((c) => c.entityId)).size > 1;
  const valueOf = (code: string, entityId: string, year: number): string => {
    const c = memberCells.find(
      (m) => m.code === code && m.entityId === entityId && m.year === year,
    );
    if (!c) return KPI_IMPORT_COPY.preview.dash;
    if (c.numericValue !== undefined) return formatKpiNumber(c.numericValue);
    return c.textValue ?? KPI_IMPORT_COPY.preview.dash;
  };

  return (
    <section aria-labelledby="kpi-import-bd-preview" data-testid="kpi-import-bd-preview">
      <h3 id="kpi-import-bd-preview" className="font-semibold mb-1">
        {b.previewTitle(groups.length)}
      </h3>
      <div className="text-sm mb-2" style={{ color: "var(--color-text-2)" }}>
        {b.previewHint}
      </div>
      <div style={{ overflowX: "auto", border: "1px solid var(--color-border)", borderRadius: 12 }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 560 }}>
          <thead>
            <tr>
              <th style={thStyle} scope="col">
                {KPI_IMPORT_COPY.preview.th.indicator}
              </th>
              {cols.map((c) => (
                <th
                  key={`${c.entityId}|${c.year.toString()}`}
                  style={{ ...thStyle, textAlign: "right" }}
                  scope="col"
                >
                  {multiEntity
                    ? `${c.year.toString()} · ${entityName.get(c.entityId) ?? ""}`
                    : c.year}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const total = indByCode.get(g.parentCode);
              const calc = calcKeys.has(g.key);
              const line = lines.get(g.key) ?? null;
              return (
                <GroupRows
                  key={g.key}
                  group={g}
                  totalLabel={total?.label ?? g.parentCode}
                  calc={calc}
                  line={line}
                  colSpan={cols.length + 1}
                  totalCells={cols.map((c) => (
                    <td
                      key={`${c.entityId}|${c.year.toString()}`}
                      style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}
                    >
                      <Cell c={total?.cells[c.entityId]?.[c.year]} unit={total?.unit ?? null} />
                    </td>
                  ))}
                  memberCells={(code) =>
                    cols.map((c) => (
                      <td
                        key={`${c.entityId}|${c.year.toString()}`}
                        style={{ ...tdStyle, textAlign: "right" }}
                      >
                        {valueOf(code, c.entityId, c.year)}
                      </td>
                    ))
                  }
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function GroupRows(x: {
  group: GroupView;
  totalLabel: string;
  calc: boolean;
  line: { matched: boolean; text: string } | null;
  colSpan: number;
  totalCells: JSX.Element[];
  memberCells: (code: string) => JSX.Element[];
}): JSX.Element {
  const first = x.group.items[0]!;
  return (
    <>
      <tr data-testid="kpi-import-bd-preview-total" style={{ background: "var(--color-surface)" }}>
        <td style={{ ...tdStyle, fontWeight: 700 }}>
          {x.totalLabel}{" "}
          {x.calc ? (
            <span style={{ fontSize: "0.75rem", fontWeight: 400, color: "var(--color-text-3)" }}>
              ƒx {b.previewTotal}
            </span>
          ) : null}
          {x.line ? (
            <div
              style={{
                fontSize: "0.75rem",
                fontWeight: 400,
                color: x.line.matched ? "var(--color-text-2)" : "var(--color-warning-text)",
              }}
              data-testid={
                x.line.matched ? "kpi-import-bd-preview-ok" : "kpi-import-bd-preview-diff"
              }
            >
              {x.line.text}
            </div>
          ) : null}
        </td>
        {x.totalCells}
      </tr>
      <tr data-testid="kpi-import-bd-preview-band">
        <td
          colSpan={x.colSpan}
          style={{
            ...tdStyle,
            paddingLeft: 10,
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "var(--color-text-3)",
            borderTop: "1px solid color-mix(in srgb, var(--color-border) 40%, transparent)",
          }}
        >
          {first.groupLabel}
          {first.groupExists ? "" : ` (${b.newGroup})`}
          {x.calc ? ` ${KPI_BREAKDOWN_COPY.calcGroupSuffix}` : ""}
        </td>
      </tr>
      {x.group.items.map((m) => (
        <tr key={m.targetKey} data-testid="kpi-import-bd-preview-member">
          <td style={{ ...tdStyle, paddingLeft: 28 }}>
            {m.memberLabel}
            {m.memberExists ? "" : ` (${b.newMember})`}
          </td>
          {x.memberCells(m.memberCode ?? m.targetKey)}
        </tr>
      ))}
    </>
  );
}
